import { ReactNode } from 'react';
import { Table } from '@tanstack/react-table';
import { TableBody } from '@/components/ui/table';
import type { DataTableRow, TableAction } from '../types';
import { DataTableRow as TableRowComponent } from './data-table-row';
import { DataTableGroupRow } from './group-row';

interface SimpleTableBodyProps<T extends DataTableRow> {
  table: Table<T>;
  selectable?: boolean;
  actions?: TableAction<T>[];
  pinActions?: boolean;
  rowClassName?: (row: T) => string;
  renderGroupRow?: (row: T, isExpanded: boolean, onToggle: () => void) => ReactNode;
  isGroupRow?: (row: T) => boolean;
}

// A flat table has nothing to expand, so a group row is always "open".
const noop = () => {};

export function SimpleTableBody<T extends DataTableRow>({
  table,
  selectable = false,
  actions = [],
  pinActions = false,
  rowClassName,
  renderGroupRow,
  isGroupRow
}: SimpleTableBodyProps<T>) {
  const totalColumns = table.getVisibleLeafColumns().length + (selectable ? 1 : 0) + (actions.length > 0 ? 1 : 0);

  return (
    <TableBody>
      {table.getRowModel().rows.map((row) =>
        isGroupRow?.(row.original) && renderGroupRow ? (
          <DataTableGroupRow key={row.id} colSpan={totalColumns} className={rowClassName?.(row.original)}>
            {renderGroupRow(row.original, true, noop)}
          </DataTableGroupRow>
        ) : (
          <TableRowComponent
            key={row.id}
            row={row}
            selectable={selectable}
            actions={actions}
            pinActions={pinActions}
            rowClassName={rowClassName}
          />
        )
      )}
    </TableBody>
  );
}
